import { Fragment, type ReactNode } from 'react';

import type {
  BulletListNode,
  InlineNode,
  ParagraphNode,
  RichTextDoc,
  TextNode,
} from '@/lib/richtext/ast';
import { parseMarkdown } from '@/lib/richtext/parseMarkdown';

interface RichTextViewProps {
  markdown: string;
  paragraphClassName?: string;
  listClassName?: string;
}

export function RichTextView({ markdown, paragraphClassName, listClassName }: RichTextViewProps) {
  const doc: RichTextDoc = parseMarkdown(markdown);
  if (doc.length === 0) return null;
  return (
    <>
      {doc.map((block, i) =>
        block.type === 'paragraph' ? (
          <Paragraph key={i} node={block} className={paragraphClassName} />
        ) : (
          <BulletList
            key={i}
            node={block}
            className={listClassName}
            paragraphClassName={paragraphClassName}
          />
        ),
      )}
    </>
  );
}

function Paragraph({ node, className }: { node: ParagraphNode; className?: string }) {
  return <p className={className ?? 'mb-2 last:mb-0'}>{node.children.map(renderInline)}</p>;
}

function BulletList({
  node,
  className,
  paragraphClassName,
}: {
  node: BulletListNode;
  className?: string;
  paragraphClassName?: string;
}) {
  return (
    <ul className={className ?? 'list-disc pl-5'}>
      {node.children.map((item, i) => (
        <li key={i}>
          {item.children.map((p, j) => (
            <Fragment key={j}>{p.children.map(renderInline)}</Fragment>
          ))}
          {paragraphClassName ? null : null}
        </li>
      ))}
    </ul>
  );
}

function renderInline(node: InlineNode, i: number): ReactNode {
  if (node.type === 'hardBreak') return <br key={i} />;
  return <TextRun key={i} node={node} />;
}

function TextRun({ node }: { node: TextNode }) {
  const marks = node.marks ?? [];
  let content: ReactNode = node.text;
  if (marks.includes('italic')) content = <em>{content}</em>;
  if (marks.includes('bold')) content = <strong>{content}</strong>;
  return <>{content}</>;
}
